import Divider from '@mui/material/Divider';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import React, { ChangeEvent, useEffect } from 'react';
import { useSearchParams } from 'react-router';

import { TabPanel } from '~components/TabPanel';
import { useNormalisedTabIndex } from '~hooks/useNormalisedTabIndex';
import AccessFilterList from '~pages/SystemManagement/AccessFilterList';
import PublicHolidayList from '~pages/SystemManagement/PublicHolidayList';
import { useAuth } from '~providers/AuthProvider';
import { AccessScope } from '~providers/AuthProvider/domain';
import { useSetPageTitleProps } from '~providers/PageTitleProvider';

import AgentList from './AgentList';
import DispositionAttributesList from './DispositionAttributesList';
import ExclusionListList from './ExclusionListList';
import SkillTypeList from './SkillTypeList';
import TransferList from './TransferList';

// Used to manage visible tab selection
enum ViewTabsType {
  Agents,
  SkillTypes,
  ExclusionLists,
  DispositionAttributes,
  AccessFilters,
  Transfers,
  PublicHoliday,
}

// default tab, fallback to this tab when no permission on specified tab
const defaultViewTab = ViewTabsType.Agents;

const a11yProps = (index: number) => ({
  'id': `tab-${index}`,
  'aria-controls': `tab-${index}`,
});

const SystemManagement = () => {
  const { hasScope } = useAuth();
  const setPageTitleProps = useSetPageTitleProps();
  const [searchParams, setSearchParams] = useSearchParams({
    show: ViewTabsType[defaultViewTab],
  });
  const tabIndex = useNormalisedTabIndex(ViewTabsType, searchParams, setSearchParams, defaultViewTab);

  // Set page title
  useEffect(() => {
    setPageTitleProps({ pageName: 'System Configuration' });
  }, []);

  const onTabChange = (e: ChangeEvent<{}>, tabIndex: number): void => {
    setSearchParams({
      show: ViewTabsType[tabIndex],
    });
  };

  return (
    <>
      <Typography variant='h4' component='h1' gutterBottom>
        System Configuration
      </Typography>

      <Tabs
        orientation='horizontal'
        variant='scrollable'
        onChange={onTabChange}
        value={tabIndex}
        indicatorColor='primary'
        aria-label='Vertical tabs example'>
        <Tab label='Agents' {...a11yProps(ViewTabsType.Agents)} value={ViewTabsType.Agents} />
        <Tab label='Skill Types' {...a11yProps(ViewTabsType.SkillTypes)} value={ViewTabsType.SkillTypes} />
        <Tab label='Exclusion Lists' {...a11yProps(ViewTabsType.ExclusionLists)} value={ViewTabsType.ExclusionLists} />
        <Tab
          label='Disposition Attributes'
          {...a11yProps(ViewTabsType.DispositionAttributes)}
          value={ViewTabsType.DispositionAttributes}
        />

        {hasScope(AccessScope.CanViewAccessFilters) && (
          <Tab label='Access Filters' {...a11yProps(ViewTabsType.AccessFilters)} value={ViewTabsType.AccessFilters} />
        )}

        {hasScope(AccessScope.CanViewTransfers) && (
          <Tab label='Transfers' {...a11yProps(ViewTabsType.AccessFilters)} value={ViewTabsType.Transfers} />
        )}
        <Tab label='Public Holiday' {...a11yProps(ViewTabsType.PublicHoliday)} value={ViewTabsType.PublicHoliday} />
      </Tabs>

      <Divider variant='fullWidth' component='hr' />

      <TabPanel value={tabIndex} index={ViewTabsType.Agents}>
        <AgentList />
      </TabPanel>

      <TabPanel value={tabIndex} index={ViewTabsType.SkillTypes}>
        <SkillTypeList />
      </TabPanel>

      <TabPanel value={tabIndex} index={ViewTabsType.ExclusionLists}>
        <ExclusionListList />
      </TabPanel>

      <TabPanel value={tabIndex} index={ViewTabsType.DispositionAttributes}>
        <DispositionAttributesList />
      </TabPanel>

      {hasScope(AccessScope.CanViewAccessFilters) && (
        <TabPanel value={tabIndex} index={ViewTabsType.AccessFilters}>
          <AccessFilterList />
        </TabPanel>
      )}

      {hasScope(AccessScope.CanViewTransfers) && (
        <TabPanel value={tabIndex} index={ViewTabsType.Transfers}>
          <TransferList />
        </TabPanel>
      )}

      <TabPanel value={tabIndex} index={ViewTabsType.PublicHoliday}>
        <PublicHolidayList />
      </TabPanel>
    </>
  );
};

export default SystemManagement;
